import { useCallback, useEffect, useState } from "react"
import { pushFormSubmitSuccess } from "@/components/client/Datalayer/utilities"

interface UseCognitoParams {
  formId: string
  cognitoInstance?: string
  followUpUrl?: string | null
  calendlyId?: string | null
  calendlyUrl?: string | null
  onSuccessCallback?: (formValues: Record<string, any>) => void
  onTrackFormSubmit?: () => void
  formLoaded?: boolean
  // setFormLoaded: (loaded: boolean) => void
  prefillData?: Record<string, unknown>
  // The DOM id of the mount target. Defaults to `cognitoForm_${formId}` for
  // back-compat, but callers that may render the *same* Cognito formId more
  // than once on a single page (e.g. a form referenced by multiple layout
  // sections) MUST pass a per-instance-unique id here — otherwise every
  // instance's `#cognitoForm_<id>` selector resolves to the same first
  // matching element in the DOM (ids aren't unique-enforced by the browser),
  // and `Cognito(...).mount()` ends up racing to mount into that one element
  // instead of each section's own container.
  elementId?: string
}

declare global {
  interface Window {
    Cognito: any
    Calendly: any
  }
}

const useCognito = ({
  formId,
  cognitoInstance,
  followUpUrl,
  calendlyId,
  calendlyUrl,
  onSuccessCallback,
  onTrackFormSubmit,
  elementId,
  // formLoaded,
  // setFormLoaded
  prefillData,
}: UseCognitoParams) => {
  const mountElementId = elementId || `cognitoForm_${formId}`
  const [scriptAdded, setScriptAdded] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [calendlyAdded, setCalendlyAdded] = useState(false)

  const getCookie = (name: string): string => {
    if (typeof document === "undefined") return ""
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
    )
    return match ? decodeURIComponent(match[1]) : ""
  }

  /**
   * Google Analytics Client ID lives in the `_ga` cookie, e.g. `GA1.2.1234567890.1600000000`.
   * The Client ID portion is the last two dot-separated segments.
   */
  const getGaClientId = (): string => {
    const raw = getCookie("_ga")
    if (!raw) return ""
    const parts = raw.split(".")
    return parts.length >= 4 ? parts.slice(-2).join(".") : raw
  }

  const getSearchParam = (
    searchParams: URLSearchParams,
    key: string,
  ): string => searchParams.get(key) || ""

  const getUTMParams = () => {
    const paramsString = window.location.search
    const searchParams = new URLSearchParams(paramsString)

    const utmParams: Record<string, string | null> = {}
    const utmKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
    ]

    utmKeys.forEach(key => {
      const formattedKey =
        "UTM" + key.slice(4).charAt(0).toUpperCase() + key.slice(5)
      utmParams[formattedKey] =
        localStorage.getItem(key) || searchParams.get(key)
    })

    return utmParams
  }

  /**
   * Sector fallback chain:
   *   1. <body data-sector="...">
   *   2. First non-locale URL path segment (e.g. `/construction/...`)
   *   3. localStorage("sector")
   */
  const getSector = (): string => {
    if (typeof document === "undefined") return ""
    const bodyAttr = document.body?.dataset?.sector
    if (bodyAttr) return bodyAttr

    const segments = window.location.pathname.split("/").filter(Boolean)
    // Skip a leading locale segment like `en` or `en-US`.
    const first = /^[a-z]{2}(-[A-Z]{2})?$/.test(segments[0] ?? "")
      ? segments[1]
      : segments[0]
    if (first) return first

    return localStorage.getItem("sector") || ""
  }

  const getLanguage = (): string => {
    if (typeof document !== "undefined") {
      const htmlLang = document.documentElement.getAttribute("lang")
      if (htmlLang) return htmlLang
    }
    if (typeof navigator !== "undefined" && navigator.language) {
      return navigator.language
    }
    return ""
  }

  const getWebIDs = () => {
    const currLocation = window?.location.href
    const searchParams = new URLSearchParams(window.location.search)
    const dataCloudMappingsStr = sessionStorage.getItem("data_cloud_mappings")
    const data_cloud_mappings = dataCloudMappingsStr
      ? JSON.parse(dataCloudMappingsStr)
      : {}

    // Prefer the data cloud mapping (populated by the shared datalayer), but
    // fall back to reading directly from URL params + cookies so the form can
    // still attribute the visitor even if the mapping hasn't hydrated yet.
    const gclid =
      data_cloud_mappings?.gclid || getSearchParam(searchParams, "gclid")
    const msclkid =
      data_cloud_mappings?.msclkid || getSearchParam(searchParams, "msclkid")
    const fbclid =
      data_cloud_mappings?.meta_id ||
      getSearchParam(searchParams, "fbclid") ||
      getCookie("_fbp")
    const linkedinid =
      data_cloud_mappings?.linkedin_id ||
      getSearchParam(searchParams, "li_fat_id") ||
      getSearchParam(searchParams, "sclid")
    const googleid = data_cloud_mappings?.google_id || getGaClientId()
    const salesforceid =
      data_cloud_mappings?.salesforce_id ||
      getCookie("sfdc-visitor-id") ||
      getCookie("BrowserId") ||
      getCookie("sf_visitor_id")

    return {
      GoogleID: googleid,
      GoogleAdsID: data_cloud_mappings?.google_ads_id || gclid,
      MetaID: fbclid,
      LinkedInID: linkedinid,
      MicrosoftClickID: msclkid,
      SalesforceID: salesforceid,
      // Legacy field names kept for forms that still reference them.
      Gclid: gclid,
      TIDSupport: currLocation.includes("sign-in-product-access") ? "YES" : "",
    }
  }

  const handleDatalayer = useCallback(
    (
      formId: string,
      formProvider: string,
      utmParams: Record<string, string | null>,
      entry: Record<string, any>,
    ) => {
      pushFormSubmitSuccess({
        formName: formId,
        formType: "cognito_form",
        formProvider,
        formId,
        formData: entry,
        utmParams,
        calendlyId,
        calendlyUrl,
      })
    },
    [calendlyId, calendlyUrl],
  )

  const handleScriptLoad = useCallback(() => {
    const api = window.Cognito(cognitoInstance)
    const form1 = api.mount(formId, `#${mountElementId}`)

    let prefillCalendly: { name: string; email: string } = {
      name: "",
      email: "",
    }
    const utmParams = getUTMParams()
    const webIDs = getWebIDs()

    const previousPath = sessionStorage.getItem("previousPath")
    const previousURL = previousPath
      ? `${window.location.origin}${previousPath}`
      : document.referrer || ""
    const formPageURL = `${window.location.origin}${window.location.pathname}`

    form1.prefill({
      ...utmParams,
      FormReferrerURL: formPageURL,
      ...webIDs,
      PreviousURL: previousURL,
      ...(prefillData ?? {}),
    })

    form1.on("beforeSubmit", function (event: { data: Record<string, any> }) {
      if (calendlyUrl) {
        prefillCalendly = {
          name: event.data.entry["Name"]
            ? `${event.data.entry["Name"]["First"]} ${event.data.entry["Name"]["Last"]}`
            : "",
          email: event.data.entry["Email"] || "",
        }
      }
    })

    form1.on("afterSubmit", function (event: { data: Record<string, any> }) {
      if (calendlyUrl) {
        handleDatalayer(formId, "Cognito", utmParams, event.data)
        onTrackFormSubmit?.()
        window.Calendly.initPopupWidget({
          url: calendlyUrl,
          prefill: {
            name: prefillCalendly.name,
            email: prefillCalendly.email,
          },
        })

        const callback: MutationCallback = (
          mutationList: MutationRecord[],
          _observer: MutationObserver,
        ): void => {
          for (const mutation of mutationList) {
            if (mutation.type === "childList") {
              mutation.removedNodes.forEach((removedNode: Node) => {
                if (
                  removedNode instanceof Element &&
                  removedNode?.className === "calendly-overlay"
                ) {
                  const url =
                    followUpUrl ?? "https://www.trimble.com/request-demo-ty"
                  window.open(url, "_self")
                }
              })
            }
          }
        }
        const observer = new MutationObserver(callback)
        observer.observe(document.getElementsByTagName("body")[0], {
          childList: true,
        })
      } else {
        handleDatalayer(formId, "Cognito", utmParams, event.data)
        onSuccessCallback?.({ ...event.data, ...utmParams })
      }
    })
  }, [
    calendlyUrl,
    cognitoInstance,
    followUpUrl,
    formId,
    handleDatalayer,
    mountElementId,
    onSuccessCallback,
    onTrackFormSubmit,
    prefillData,
  ])

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (scriptAdded) {
      if (!scriptLoaded) {
        handleScriptLoad()
        setScriptLoaded(true)
      }
      return
    }

    if (window.Cognito) {
      setScriptAdded(true)
      return
    }

    const script = document.createElement("script")
    script.defer = true
    script.src = "https://www.cognitoforms.com/f/seamless.js"
    document.head.appendChild(script)
    script.onload = () => {
      setScriptAdded(true)
    }

    const link = document.createElement("link")
    link.href = "https://assets.calendly.com/assets/external/widget.css"
    link.rel = "stylesheet"
    document.head.appendChild(link)

    const script2 = document.createElement("script")
    script2.src = "https://assets.calendly.com/assets/external/widget.js"
    script2.defer = true
    document.head.appendChild(script2)
    script2.onload = () => {
      setCalendlyAdded(true)
    }
  }, [calendlyAdded, handleScriptLoad, scriptAdded, scriptLoaded])
  /* eslint-enable react-hooks/set-state-in-effect */
}

export default useCognito
